import {
  AddMembersPayload,
  addOrganizationMembers,
  archiveOrganizationMembers,
  createNewTeam,
  EditMembersPayload,
  editOrganizationMember,
  fetchOrganizationMembersCount,
  fetchOrganizationMembersPaginated,
  fetchOrganizationSettings,
  fetchPlanLimits,
  PostgrestEndpoints,
  reInviteOrganizationMembers,
} from '@/requests';
import { AnalyticsService, EVENTS } from '@/services';
import { OrganizationMember, OrganizationSettings } from '@/types';
import { camelCaseKeys } from '@/utils';

import { postgrestApi } from '../postgrest-api';
import { PostgrestApiTags } from '../store.constants';
import { handleOnQueryStartedError } from '../store.utils';
import { PlanLimits } from './member-management-api.types';
import { transformOrganizationMember } from './member-management-api.utils';

export const memberManagementApi = postgrestApi.injectEndpoints({
  endpoints: builder => ({
    fetchOrganizationMembersCount: builder.query<number, void>({
      query: fetchOrganizationMembersCount,
      transformResponse: (response: any, meta: any) =>
        parseInt(
          meta.response?.headers.get('content-range')?.split('/')[1] ?? '0',
          10,
        ),
    }),
    fetchOrganizationMembers: builder.infiniteQuery<
      OrganizationMember[],
      { limit: number },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _, lastPageParam, __, queryArg) =>
          lastPage.length < queryArg.limit
            ? undefined
            : lastPageParam + queryArg.limit,
      },
      query: ({ queryArg, pageParam }) =>
        fetchOrganizationMembersPaginated({
          limit: queryArg.limit,
          offset: pageParam,
        }),
      transformResponse: (response: unknown[]) =>
        response.map(transformOrganizationMember),
      providesTags: [PostgrestApiTags.ORGANIZATION_MEMBERS],
    }),
    fetchPlanLimits: builder.query<PlanLimits, void>({
      query: fetchPlanLimits,
      transformResponse: (response: any) => ({
        ...camelCaseKeys(response.limits),
        usedCredits: response.used_credits,
        usedSeats: response.used_seats,
      }),
      providesTags: [PostgrestApiTags.PLAN],
    }),
    fetchOrganizationSettings: builder.query<OrganizationSettings, void>({
      query: fetchOrganizationSettings,
      transformResponse: (response: any) => camelCaseKeys(response[0]),
    }),
    archiveOrganizationMembers: builder.mutation<boolean, number[]>({
      query: archiveOrganizationMembers,
      invalidatesTags: [PostgrestApiTags.ORGANIZATION_MEMBERS],
    }),
    addOrganizationMembers: builder.mutation<boolean, AddMembersPayload>({
      query: addOrganizationMembers,
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          AnalyticsService.logEvent(
            EVENTS.MEMBER_MANAGEMENT_EVENTS.ADD_MEMBERS.INTENT,
          );
          await queryFulfilled;
          AnalyticsService.logEvent(
            EVENTS.MEMBER_MANAGEMENT_EVENTS.ADD_MEMBERS.SUCCESS,
          );
        } catch (error: any) {
          AnalyticsService.logEvent(
            EVENTS.MEMBER_MANAGEMENT_EVENTS.ADD_MEMBERS.FAIL,
            { error: error?.error?.data?.message },
          );
          handleOnQueryStartedError(error);
        }
      },
      invalidatesTags: [PostgrestApiTags.ORGANIZATION_MEMBERS],
    }),
    editOrganizationMember: builder.mutation<boolean, EditMembersPayload>({
      query: editOrganizationMember,
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          AnalyticsService.logEvent(
            EVENTS.MEMBER_MANAGEMENT_EVENTS.EDIT_MEMBER.INTENT,
          );
          await queryFulfilled;
          AnalyticsService.logEvent(
            EVENTS.MEMBER_MANAGEMENT_EVENTS.EDIT_MEMBER.SUCCESS,
          );
        } catch (error: any) {
          AnalyticsService.logEvent(
            EVENTS.MEMBER_MANAGEMENT_EVENTS.EDIT_MEMBER.FAIL,
            { error: error?.error?.data?.message },
          );
          handleOnQueryStartedError(error);
        }
      },
      invalidatesTags: [PostgrestApiTags.ORGANIZATION_MEMBERS],
    }),
    reInviteOrganizationMembers: builder.mutation<boolean, number[]>({
      query: reInviteOrganizationMembers,
      transformResponse: (response: any) => response.data,
      invalidatesTags: [PostgrestApiTags.ORGANIZATION_MEMBERS],
    }),
    updateOrganizationName: builder.mutation<
      boolean,
      { organizationName: string; organizationId: string }
    >({
      query: ({ organizationName, organizationId }) => ({
        url: PostgrestEndpoints.UPDATE_ORGANIZATION_NAME,
        method: 'POST',
        body: {
          organization_id: organizationId,
          name: organizationName,
        },
      }),
    }),
    createNewTeam: builder.mutation<boolean, { companyName: string }>({
      query: createNewTeam,
      invalidatesTags: [PostgrestApiTags.PLAN, PostgrestApiTags.USER_PROFILE],
    }),
  }),
});

export const {
  useReInviteOrganizationMembersMutation,
  useFetchOrganizationMembersCountQuery,
  useFetchOrganizationMembersInfiniteQuery,
  useFetchPlanLimitsQuery,
  useFetchOrganizationSettingsQuery,
  useArchiveOrganizationMembersMutation,
  useAddOrganizationMembersMutation,
  useEditOrganizationMemberMutation,
  useCreateNewTeamMutation,
  useUpdateOrganizationNameMutation,
} = memberManagementApi;
